import { Layout } from '~/components/Layout'
import { Colors } from '~/styles/colors'
import { useLoaderData } from '@remix-run/react'
import type { LinksFunction, LoaderFunction, MetaFunction } from '@remix-run/node'
import { getHomePage } from '~/queries/getHomePage'
import { PageConstructor } from '~/components/main-page/PageConstructor'
import { Notification, notificationLinks } from '~/components/Notification'
import { getNotification } from '~/queries/getNotification'
import {
  PageContext,
  preparePageContext,
  prepareRemixRequest,
  prepareRobotsMetadata,
} from '~/utils/routes/routeHelper'
import ContentfulClientManager from '../libs/contentful/ContentfulClientManager'

import mainStyles from '../components/main-page/main.css?url'
import { Entry } from 'contentful'
import {
  TypeHomePageSkeleton,
  TypePromoBannerSkeleton,
  TypeWebsiteNotificationSkeleton,
} from '~/types/contentful'
import { TopLevelBlock } from '@contentful/rich-text-types'
import { Document } from '@contentful/rich-text-types'

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: mainStyles },
  ...notificationLinks(),
]

export const loader: LoaderFunction = async ({ request }) => {
  const url = prepareRemixRequest(request)

  const HomePageData = await getHomePage({})
  const notificationInfo = await getNotification()

  const totalJobSearchBanner =
    await ContentfulClientManager.getContentfulEntry<TypePromoBannerSkeleton>(
      `7Ha9j9NOo3tVNC1n1HYmGn`,
      {
        include: 3,
        preview: true,
      },
    )

  const totalJobSearchPromo =
    await ContentfulClientManager.getContentfulEntry<TypePromoBannerSkeleton>(
      `7DwVyO4lv7H79d1e9zOjRj`,
      {
        include: 3,
        preview: true,
      },
    )

  const contextValue = preparePageContext(url)

  return { HomePageData, contextValue, notificationInfo, totalJobSearchBanner, totalJobSearchPromo }
}

type HomeLoaderData = {
  HomePageData?: Entry<TypeHomePageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
  contextValue: PageContext
  notificationInfo?: Entry<TypeWebsiteNotificationSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
  totalJobSearchBanner?: Entry<TypePromoBannerSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
  totalJobSearchPromo?: Entry<TypePromoBannerSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const { HomePageData, contextValue } = data || {}

  let metaTitle, metaDescription

  if (HomePageData) {
    ;({ metaTitle, metaDescription } = HomePageData.fields)
  }

  const metaRobots = prepareRobotsMetadata(contextValue.url)

  return [
    { title: metaTitle ? `${metaTitle} · Career.io` : '' },
    {
      name: 'description',
      content: metaDescription,
    },
    { name: 'robots', content: metaRobots },
  ]
}

export default function Home() {
  const { HomePageData, notificationInfo, totalJobSearchBanner } = useLoaderData<
    typeof loader
  >() as HomeLoaderData

  return (
    <Layout headerBgColor={Colors.White}>
      <Layout.Content style={{ backgroundColor: Colors.White }}>
        {notificationInfo && <Notification notificationInfo={notificationInfo} />}
        {HomePageData && totalJobSearchBanner && (
          <PageConstructor
            sections={(HomePageData.fields.pageContent as Document)?.content as TopLevelBlock[]}
            banner={totalJobSearchBanner.fields}
          />
        )}
      </Layout.Content>
    </Layout>
  )
}
